import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Page, Pageable } from "../admin/AdminSlice"
import { RootState } from "../../app/store"
import { API_BASE } from "../../app/const"
import axios from "axios"
import { fireauth } from "../auth/AuthSlice"
import { clear } from "console"
import { stat } from "fs"

export interface CustomizeCustomJson {
  [key: string]: any
}
export interface Customize {
  id?: string
  slug?: string
  userId?: string
  userEmail?: string
  primaryColor: string
  secondaryColor: string
  tertiaryColor: string
  logoB64?: string
  logoUrl?: string
  logoS3?: string
  logoObjFit: string
  logoWidth: number
  logoHeight: number
  customJson?: CustomizeCustomJson
  notes?: string
  isActive?: boolean
}

export interface S3PutRsp {
  presignedUrl: string
  filename: string
  fault: string
}

export interface CustomizeState {
  customizeItems?: Page<Customize>
  data?: Customize
  loading?: boolean
  error?: string
  isPreview?: boolean
  logo?: S3PutRsp
}

const initialState: CustomizeState = {}

export const getCustomizeAsync = createAsyncThunk(
  'customize/getCustomizeAsync', async ({ page, mode }: { page: Pageable, mode: 'all' | 'me' }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/customize/${mode}`, {
        params: page,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const saveCustomizeAsync = createAsyncThunk(
  'customize/saveCustomizeAsync', async (customize: Customize, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.post(`${API_BASE}apps/customize`, customize, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const uploadLogoAsync = createAsyncThunk(
  'customize/uploadLogoAsync', async (file: File, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const formData = new FormData()
      formData.append('file', file)
      const response = await axios.post(`${API_BASE}apps/customize/logo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

const customizeSlice = createSlice({
  name: 'customize',
  initialState,
  reducers: {
    updateCustomize: (state, action: PayloadAction<Customize>) => {
      state.data = action.payload
    },
    resetCustomize: (state) => {
      state = initialState
    },
    clearCustomize: (state) => {
      state.data = undefined
    },
    setCustomize: (state, action: PayloadAction<Customize>) => {
      state.data = action.payload
    },
    setIsPreview: (state, action: PayloadAction<boolean>) => {
      state.isPreview = action.payload
    },
    clearLogo: (state) => {
      state.logo = undefined
      state.customizeItems?.content?.forEach((item) => {
        item.logoB64 = ''
        item.logoUrl = ''
        item.logoS3 = ''
      })
    }
  }, extraReducers: (builder) => {
    builder
      .addCase(getCustomizeAsync.pending, (state) => {
        state.loading = true
      })
      .addCase(getCustomizeAsync.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.customizeItems = action.payload
        }
      })
      .addCase(getCustomizeAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      }).addCase(uploadLogoAsync.pending, (state) => {
        state.loading = true
      }).addCase(uploadLogoAsync.fulfilled, (state, action) => {
        state.loading = false
        state.logo = action.payload
      }).addCase(uploadLogoAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      }).addCase(saveCustomizeAsync.pending, (state) => {
        state.loading = true
      }).addCase(saveCustomizeAsync.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload
      }).addCase(saveCustomizeAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})


export const getLogoObjFit = (logoObjFit: string | undefined) => {
  switch (logoObjFit) {
    case 'fill': return 'cover'
    case 'contain': return 'contain'
    case 'cover': return 'cover'
    case 'none': return 'none'
    case 'scale-down': return 'scale-down'
    default: return 'cover'
  }
}

export default customizeSlice.reducer
export const selectCustomize = (state: RootState) => state.customize.data
export const selectCustomizedItems = (state: RootState) => state.customize.customizeItems
export const selectIsPreview = (state: RootState) => state.customize.isPreview
export const selectLogo = (state: RootState) => state.customize.logo
export const { updateCustomize, resetCustomize, clearCustomize, setCustomize, setIsPreview, clearLogo } = customizeSlice.actions